import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TrendingUp, TrendingDown, DollarSign, Wallet, AlertTriangle, CheckCircle2, Loader2, Sparkles, Copy, Check, Newspaper, ExternalLink, RefreshCw } from 'lucide-react';
import { reports, news } from '../../lib/api';
import { toast } from 'sonner';

const GREEN = '#8dd110';
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ icon: Icon, label, value, color }) => (
  <Card className="p-3 border-slate-200 flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}1a`, color }}><Icon className="w-5 h-5" /></div>
    <div className="min-w-0"><div className="text-xl font-extrabold text-slate-900 leading-none tabular-nums">{value}</div><div className="text-[11px] text-slate-500 mt-0.5">{label}</div></div>
  </Card>
);

function NewsCard() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await news.snapshot(); setItems(r.headlines || []); }
    catch { setItems([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Card className="p-5 border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-bold text-slate-900"><Newspaper className="w-4 h-4" style={{ color: GREEN }} />What's going on — quick news snapshot</div>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="gap-1.5">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</Button>
      </div>
      {items === null || loading ? (
        <div className="py-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">No headlines available right now. Add RSS news feeds in the AI Assistant → Auto-Update tab.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((h, i) => (
            <a key={i} href={h.link || '#'} target="_blank" rel="noreferrer" className="flex items-start gap-2 py-2 group">
              <span className="text-[10px] font-bold text-slate-400 mt-1 flex-shrink-0 w-24 truncate">{h.source}</span>
              <span className="text-sm text-slate-800 group-hover:text-[#5b8523] flex-1">{h.title}</span>
              {h.link && <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#5b8523] mt-1 flex-shrink-0" />}
            </a>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function BusinessBriefing() {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async (p = period) => {
    setBusy(true); setData(null);
    try { setData(await reports.businessSummary(p)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate the summary'); }
    finally { setBusy(false); }
  };
  useEffect(() => { generate('month'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const m = data?.metrics;
  const copy = async () => { try { await navigator.clipboard.writeText(data?.narrative || ''); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.error('Copy failed'); } };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Your business at a glance — profit &amp; loss, jobs, and cash, with a plain-English summary.</p>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={(v) => { setPeriod(v); generate(v); }}>
            <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="month">This month</SelectItem><SelectItem value="lastmonth">Last month</SelectItem><SelectItem value="year">This year</SelectItem></SelectContent>
          </Select>
          <Button onClick={() => generate()} disabled={busy} className="gap-1.5 text-white" style={{ backgroundColor: GREEN }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}Generate
          </Button>
        </div>
      </div>

      {busy && !m ? (
        <div className="py-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : m ? (
        <>
          <div className="text-sm font-semibold text-slate-700">{m.period}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            <Stat icon={TrendingUp} label="Revenue (invoiced)" value={money(m.revenue)} color="#10b981" />
            <Stat icon={TrendingDown} label="Expenses" value={money(m.expenses)} color="#ef4444" />
            <Stat icon={m.profit >= 0 ? TrendingUp : TrendingDown} label={m.profit >= 0 ? 'Net Profit (gain)' : 'Net Loss'} value={money(m.profit)} color={m.profit >= 0 ? '#0d542b' : '#ef4444'} />
            <Stat icon={DollarSign} label="Collected" value={money(m.collected)} color="#8dd110" />
            <Stat icon={Wallet} label="Open A/R" value={money(m.ar)} color="#f59e0b" />
            <Stat icon={AlertTriangle} label="Past due" value={money(m.pastDue)} color="#ef4444" />
          </div>
          <div className="text-xs text-slate-500">Jobs completed: <b>{m.jobsCompleted}</b> of {m.jobsTotal} scheduled{m.topServices?.length ? ` · Top service: ${m.topServices[0].name} (${money(m.topServices[0].revenue)})` : ''}</div>

          <Card className="p-4 border-slate-200 bg-slate-50/60">
            <div className="flex items-center justify-between mb-1">
              <Badge className={`border-0 ${data.source === 'ai' ? 'bg-[#8dd110]/20 text-[#5b8523]' : 'bg-slate-200 text-slate-600'}`}>{data.source === 'ai' ? 'AI summary' : 'Summary (add an AI key for narrative)'}</Badge>
              <Button size="sm" variant="outline" onClick={copy} className="gap-1.5">{copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}{copied ? 'Copied' : 'Copy'}</Button>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">{data.narrative}</pre>
          </Card>
        </>
      ) : null}

      <NewsCard />
    </div>
  );
}
